import { decode } from 'entities';

import { inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { ApWindowService } from '@ap/cdk';

import { ApContentItem } from './content-item';
import { ContentPrefix } from './content.model';

/**
 * @ngdoc service
 * @name ApContentService
 * @module ApContentModule
 * @description The fpContentService is a storage for multi-lingual editable content for labels.
 * This service is also used by {@link ApContentComponent}, see that component for more details and usage information.
 */
@Injectable({
    providedIn: 'root'
})
export class ApContentService {
    private readonly windowService = inject(ApWindowService);
    private readonly sanitizer = inject(DomSanitizer);

    private contentPrefix: string;
    private readonly theme: string;
    private readonly contentItems: ApContentItem = {};
    private readonly headlessContent: { [key: string]: string } = {};

    constructor() {
        try {
            this.theme = sessionStorage.getItem('theme');
        } catch (e) {
            console.info('sessionStorage not available');
        }
    }

    /**
     * @ngdoc method
     * @methodOf ApContentService
     * @name ApContentService#addContentItems
     * @description Adds a key value map of content items.
     * @param contentItems the content items to add.
     */
    public addContentItems(contentItems: ApContentItem): void {
        Object.keys(contentItems).forEach((key) => {
            // In angular component initialisation is top-down so the previously added component content
            // is added over the newly added component content
            if (this.contentItems[key]) {
                if (!this.contentItems[key].default) {
                    this.contentItems[key] = {
                        ...contentItems[key],
                        ...this.contentItems[key]
                    };
                }
            } else {
                this.contentItems[key] = { ...contentItems[key] };
            }
        });
    }

    public addHeadlessContentItems(contentItems: { [key: string]: string }): void {
        Object.keys(contentItems).forEach((key) => (this.headlessContent[key] = contentItems[key]));
    }

    /**
     * @ngdoc method
     * @methodOf ApContentService
     * @name ApContentService#removeContentItems
     * @description Removes a key value map of content items.
     * @param contentItems the content items to remove.
     */
    public removeContentItems(contentItems: ApContentItem): void {
        Object.keys(contentItems).forEach((key) => {
            delete this.contentItems[key];
        });
    }

    /**
     * Return string value for specific content key.
     * When set, returns theme value, otherwise default.
     * Optionally interpolates value.
     */
    public get(key: string, interpolationParams?: { [key: string]: string }): string {
        const value = this.getValue(key);
        return interpolationParams ? this.interpolate(value, interpolationParams) : value;
    }

    /**
     * @ngdoc method
     * @methodOf ApContentService
     * @name ApContentService#interpolate
     * @description Gets the value for the key matching all the given criteria and interpolates the data.
     * @param value The value.
     * @param params The parameters
     * @returns value The value
     */
    public interpolate(value: string, params: { [key: string]: string }): string {
        // Also see:
        // https://stackoverflow.com/a/31999948
        // https://stackoverflow.com/a/41118285
        const sanitizedInterpolation = value.replace(/\${(\w+?)}/g, (_, v) => this.sanitizer.sanitize(SecurityContext.HTML, params[v]));
        return decode(sanitizedInterpolation);
    }

    /**
     * @ngdoc method
     * @methodOf ApContentService
     * @name ApContentService#setContentPrefix
     * @description Sets the contentPrefix
     * @param contentPrefix the content prefix.
     */
    public setContentPrefix(contentPrefix: string): void {
        const uppercaseContentPrefix = contentPrefix.toUpperCase();
        if (ContentPrefix[uppercaseContentPrefix]) {
            this.contentPrefix = ContentPrefix[uppercaseContentPrefix];
        }
    }

    /**
     * @ngdoc method
     * @methodOf ApContentService
     * @name getEntriesWithKey#setContentPrefix
     * @description Gets all keys in content starting with a contentKey
     * @param contentKey the content key to look for.
     */
    public getEntriesWithKey(contentKey: string): string[] {
        return Object.keys(this.contentItems).filter((key) => key.startsWith(contentKey));
    }

    private getValue(key: string): string {
        const path = this.windowService.getWindow().location.href;
        const queryParams = this.getQueryParams(path);
        if (queryParams['display-content-keys']) {
            return key;
        }

        const headlessItem = this.headlessContent[key];
        if (headlessItem) {
            return headlessItem;
        }

        const contentItem = this.contentItems[key];
        if (!contentItem) {
            return key;
        }

        let item = contentItem[this.theme] ?? contentItem?.default;

        const prefixContent = contentItem[this.contentPrefix] as { [theme: string]: string };
        item = prefixContent?.[this.theme] ?? prefixContent?.default ?? item;

        return item as string;
    }

    /**
     * Get getQueryParams of url
     * @param url
     * @private
     */
    private getQueryParams(url: string): Record<string, string> {
        const [, ...params] = url.split(/[?&]/);

        return params.reduce((acc, curr) => {
            const split = curr.split('=');
            return { ...acc, [split[0]]: split[1] };
        }, {});
    }
}
